import React from "react";
import styled, { css, keyframes } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, shadow } from "src/theme/ui";

/**
 * A six-digit code, drawn as six cells.
 *
 * It was one text box: a `react-input-mask` field 300px wide with no length,
 * no placeholder and no label, whose mask had been commented out while
 * `alwaysShowMask` was left on. Nothing about it said six digits, nothing
 * stopped a fiftieth character, and `inputMode` and `autocomplete` were both
 * unset, so on the phone that had just received the code by SMS the reader
 * got a full keyboard and no offer to fill it in.
 *
 * Six cells and ONE real input over them, invisible and full-bleed, holding
 * the whole code. The cells are decoration: they cannot take focus, so tab
 * passes the field once rather than six times, a paste of "123456" lands in
 * a single value, and the browser's own one-time-code autofill works because
 * there is exactly one field to fill.
 *
 * The same field the webapp's email verification uses, so the code box a
 * client meets on the way in and the one they meet at every order are the
 * same object.
 */

const CELLS = [0, 1, 2, 3, 4, 5];

const Field = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * The real control, laid over the cells: the only thing that takes focus,
 * and invisible because the cells below draw what it holds.
 */
const Entry = styled.input`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  outline: none;
  /* A tap anywhere on the row reaches it, including the gaps. */
  cursor: text;

  /* Safari paints its own ground on an autofilled field, which would cover
     the cells underneath. */
  &:-webkit-autofill {
    -webkit-text-fill-color: transparent;
    transition: background-color 10000s;
  }
`;

const Cells = styled.div`
  display: flex;
  gap: 8px;
`;

const blink = keyframes`
  0%, 45% { opacity: 1; }
  55%, 100% { opacity: 0; }
`;

/* Takes the cell's own ink, so a rejected row does not grow a third colour
   at the point the reader is typing. */
const Caret = styled.span`
  width: 2px;
  height: 20px;
  border-radius: 2px;
  background: currentColor;
  animation: ${blink} 1.1s steps(1, end) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * One digit's box, in four states.
 *
 * Resting is a quiet field; a filled one carries its digit in ink; the one
 * the reader is on takes the accent edge under the canvas's own focus ring,
 * which is the treatment every other field here wears. A rejected code turns
 * the whole row, because neither the reader nor we know which digit was
 * wrong.
 */
const Cell = styled.div<{
  $filled?: boolean;
  $active?: boolean;
  $invalid?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  height: 48px;
  border: 1px solid ${line.field};
  border-radius: ${radii.control}px;
  background: ${colors.gray[50]};
  color: ${colors.gray[400]};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;

  ${({ $filled }) =>
    $filled &&
    css`
      background: ${colors.white};
      color: ${colors.gray[900]};
    `}

  ${({ $active }) =>
    $active &&
    css`
      border-color: ${colors.primary[500]};
      background: ${colors.white};
      color: ${colors.gray[900]};
      box-shadow: ${shadow.focusRing};
    `}

  /* Last, so it wins, and it takes the focus ring with it: a rejected row
     with the accent ring still on the cell you are in says two things at
     once. Error edge and error ring, which is what every other field on this
     canvas does when it is both focused and wrong. */
  ${({ $invalid, $active }) =>
    $invalid &&
    css`
      border-color: ${$active ? colors.error[500] : colors.error[300]};
      background: ${colors.error[25]};
      color: ${colors.error[700]};
      box-shadow: ${$active ? `0 0 0 4px ${colors.error[100]}` : "none"};
    `}
`;

export interface CodeInputProps {
  value?: string;
  /** The digits typed so far, never an event: callers set a field value. */
  onChange: (code: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Named for a screen reader; the instruction above the row is the label. */
  label: string;
  name?: string;
  id?: string;
  autoFocus?: boolean;
  invalid?: boolean;
}

const CodeInput: React.FC<CodeInputProps> = ({
  value = "",
  onChange,
  onBlur,
  label,
  name,
  id,
  autoFocus,
  invalid,
}) => {
  const [focused, setFocused] = React.useState(false);
  const code = String(value ?? "");

  return (
    <Field>
      <Entry
        autoFocus={autoFocus}
        aria-label={label}
        name={name}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(event) =>
          onChange(event.target.value.replace(/\D/g, "").slice(0, 6))
        }
        onFocus={() => setFocused(true)}
        onBlur={(event) => {
          setFocused(false);
          if (onBlur) onBlur(event);
        }}
      />
      <Cells aria-hidden="true">
        {CELLS.map((index) => {
          const filled = index < code.length;
          /* Lit only while the field has focus: an untouched form should not
             look like it is waiting on you. */
          const active =
            focused && index === Math.min(code.length, 5) && !filled;
          return (
            <Cell
              key={index}
              $filled={filled}
              $active={active}
              $invalid={invalid}
            >
              {filled ? code[index] : active ? <Caret /> : null}
            </Cell>
          );
        })}
      </Cells>
    </Field>
  );
};

export default CodeInput;
